import React from 'react';
import { Box, Card, Chip, Button, Typography, Skeleton } from '@mui/material';
import { Refresh, Download } from '@mui/icons-material';
import { VendorLogo } from '../../../components/VendorLogo';
import { brandHome } from '../brandHomeTokens';
import { SEVERITY_TONES } from '../severityTones';

export interface MetaItem {
  label: string;
  value: string;
  muted?: boolean;
}

interface BrandHeaderCardProps {
  brandName: string;
  statusLabel: string;
  subline: string;
  meta: MetaItem[];
  loading?: boolean;
  onRefresh?: () => void;
  refreshing?: boolean;
}

export const BrandHeaderCard: React.FC<BrandHeaderCardProps> = ({
  brandName,
  statusLabel,
  subline,
  meta,
  loading,
  onRefresh,
  refreshing,
}) => {
  const positive = SEVERITY_TONES.positive;

  return (
    <Card sx={{ borderRadius: `${brandHome.radius.card}px`, border: `1px solid ${brandHome.color.border}`, boxShadow: brandHome.shadow.card, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '18px', p: '18px 22px', flexWrap: 'wrap' }}>
        <VendorLogo brand={brandName} size={52} showTooltip={false} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Typography sx={{ fontSize: '23px', fontWeight: 700, letterSpacing: '-0.02em', color: brandHome.color.ink }}>
              {brandName}
            </Typography>
            <Chip
              label={statusLabel}
              size="small"
              sx={{ bgcolor: positive.bg, border: `1px solid ${positive.border}`, color: positive.ink, fontWeight: 700, fontSize: '11.5px' }}
            />
          </Box>
          {loading ? (
            <Skeleton variant="text" width={280} />
          ) : (
            <Typography sx={{ fontSize: '13.5px', color: brandHome.color.muted }}>{subline}</Typography>
          )}
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex', gap: '9px' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Refresh />}
            onClick={onRefresh}
            disabled={refreshing}
            sx={{ borderColor: brandHome.color.border, color: brandHome.color.ink, textTransform: 'none', borderRadius: `${brandHome.radius.control}px` }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Download />}
            disabled
            sx={{ bgcolor: brandHome.color.infoSolid, textTransform: 'none', borderRadius: `${brandHome.radius.control}px` }}
          >
            Export
          </Button>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', borderTop: `1px solid ${brandHome.color.hairline}`, bgcolor: brandHome.color.subtleSurface }}>
        {meta.map((item) => (
          <Box
            key={item.label}
            sx={{ flex: '1 1 170px', minWidth: 0, p: '11px 22px', borderRight: `1px solid ${brandHome.color.hairline}`, display: 'flex', flexDirection: 'column', gap: '2px' }}
          >
            <Typography sx={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: brandHome.color.faint }}>
              {item.label}
            </Typography>
            <Typography sx={{ fontSize: '13px', fontWeight: 500, color: item.muted ? brandHome.color.faint : brandHome.color.ink }}>
              {item.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Card>
  );
};
